import moment from 'moment';
import {
  BookingState,
  DriveEditableStatus,
  FacilityDriver,
  FacilityDriverMapped,
  FacilityVehicle,
  FacilityVehicleMapped,
  IAgencyServicePoints,
  IBookingDetailsApi,
  IBookingDetails,
  IBookingDriveApi,
  IBookingDrive,
  IBookingListApi,
  IBookingList,
  IBookingRouteApi,
  ICountryReportApi,
  ICountryReport,
  IDriveCostsApi,
  IDriveCosts,
  IDriveWithWorkflowApi,
  IDriveWithWorkflow,
  IDriver,
  IDriverApi,
  IDriverDetailsApi,
  IDriverDetails,
  IFacilityApi,
  IFacility,
  IFeedbacksList,
  IFeedbacksListApi,
  IFormDriveEdit,
  IHistoryInvoiceApi,
  IHistoryInvoice,
  IIndividualInvoiceDetailsApi,
  IIndividualInvoiceDetails,
  IIndividualInvoiceTableApi,
  IIndividualInvoiceTable,
  IMonthlySummaryApi,
  IMonthlySummary,
  IPlannerDrivesApi,
  IPlannerDrives,
  IPlannerEvent,
  IPlannerResources,
  IPlannerVehiclesApi,
  IReportDetailsListApi,
  IReportDetailsList,
  ISharedDrivesApi,
  ISummaryListApi,
  ISummaryList,
  IUpdateDrive,
  IUserInfoApi,
  IUserInfo,
  IVehicleApi,
  IVehicle,
  IVehicleDetailsApi,
  IVehicleDetails,
  IVehicleCostsApi,
  IVehicleCosts,
} from '@common/interfaces';
import { SessionStorageKeys, DATE_FORMAT } from '@common/constants';
import { formatDateString } from './date';

class Serializer {
  private static getEventClassName(status: DriveEditableStatus): string {
    switch (status) {
      case DriveEditableStatus.Blue:
        return 'fc-event-blue';
      case DriveEditableStatus.Green:
        return 'fc-event-green';
      default:
        return 'fc-event-grey';
    }
  }

  // Common ==============================================================
  public static formatFacility(data: IFacilityApi): IFacility {
    return {
      agency: data.agency,
      agencyId: data.agency_id,
      agencyServicePoints: data.agency_service_points?.map((item: IAgencyServicePoints) => ({
        agencyId: item.agency_id_on_hbh,
        dateJoined: item.date_joined,
        emails: item.emails,
        id: item.uuid,
      })),
      avgRating: data.avg_rating,
      city: data.city_name,
      country: data.country_name,
      costRecoveryByVehicleType: data.cost_recovery_by_vehicle_type,
      email: data.email,
      emergencyLevel: data.emergency_level,
      facilityId: (sessionStorage.getItem(SessionStorageKeys.FACILITY) as string) || '',
      feedbacks: data.enabled_feedbacks,
      linkFleetManagement: data.fleet_management_url,
      linkMobilityLogbook: data.mobility_logbook_url,
      linkVehicleTrackingSystem: data.vehicle_tracking_system,
      locationFiles: data.location_files,
      name: data?.shared_with_agencies?.length > 1 ? data.name : '',
      securityEmails: data.security_emails,
      uuid: data.uuid,
      sharedWithAgencies: data?.shared_with_agencies || [],
    };
  }

  public static formatUser(data: IUserInfoApi): IUserInfo {
    return {
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
    };
  }

  // Booking =============================================================
  public static formatBooking(data: IBookingListApi): IBookingList {
    return {
      agencyFk: data.agency_fk,
      agencyName: data.agency,
      agencyShortName: data.agency_short_name,
      dispatchDate: data.dispatch_date || '',
      firstName: data.first_name,
      forwardKey: data.forward_key,
      forwardLabel: data.forward_label,
      id: data.uuid,
      isRecurring: data.is_recurring,
      lastName: data.last_name,
      pax: data.number_of_pax,
      pickupDate: data.pickup_date
        ? formatDateString(data.pickup_date, 'yyyy-MM-dd', 'dd/MM/yyyy')
        : '',
      pickupTime: data.pickup_time,
      requestingUnit: data.requesting_unit,
      state: data.state as BookingState,
      transferType: data.transfer_type,
      typeOfTrip: data.type_of_trip,
    };
  }

  public static formatBookingDetails(data: IBookingDetailsApi): IBookingDetails {
    return {
      agency: data.agency,
      agencyFk: data.agency_fk
        ? {
            agencyId: data.agency_fk.agency_id_on_hbh,
            completeName: data.agency_fk.complete_name,
            id: data.agency_fk.uuid,
            shortName: data.agency_fk.short_name,
          }
        : null,
      attachments:
        data.attachments?.map((attachment) => ({
          attachmentLink: attachment.attachment_link,
          isPassport: attachment.is_passport,
        })) || [],
      bookingAgentEmail: data.booking_agent_email,
      bookingAgentFirstName: data.booking_agent_first_name,
      bookingAgentLastName: data.booking_agent_last_name,
      bookingAgentUsername: data.booking_agent_username,
      dispatchDate: data.dispatch_date
        ? formatDateString(data.dispatch_date, 'dd/MM/yyyy', 'dd MMM yyyy')
        : '',
      drives:
        data.drives?.map((drive: IBookingDriveApi, idx: number) => ({
          driveNumber: idx,
          driver: drive.driver,
          dropoffDate: drive.dropoff_date,
          dropoffLocation: drive.dropoff_location,
          dropoffTime: drive.dropoff_time,
          dropoffTown: drive.dropoff_town,
          id: drive.uuid,
          isActive: drive.is_active,
          isCarpooled: drive.is_carpooled,
          isRideShared: drive.is_ride_shared,
          pickupDate: drive.pickup_date,
          pickupLocation: drive.pickup_location,
          pickupTime: drive.pickup_time,
          pickupTown: drive.pickup_town,
          signatureCode: drive.signature_code,
          vehicle: drive.vehicle,
        })) || [],
      email: data.email,
      facilityId: data.facility_id,
      firstName: data.first_name,
      flightArrivalDepartureTime: data.flight_arrival_departure_time,
      flightNumber: data.flight_number,
      forwarded: data.forwarded,
      id: data.uuid,
      indexNumber: data.index_number,
      lastName: data.last_name,
      managerEmail: data.manager_email,
      pax: data.n_of_passengers,
      passengersInfo: data.passengers_info?.map((passenger) => ({
        ...passenger,
        firstName: passenger.first_name,
        lastName: passenger.last_name,
        phoneNumber: passenger.phone_number,
        requestingUnit: passenger.requesting_unit_display,
      })),
      phoneNumber: data.phone_number,
      purpose: data.purpose,
      purposeDisplay: data.purpose_display,
      recurringData: data.recurring
        ? {
            startDate: data.recurring.start_date,
            endDate: data.recurring.end_date,
            frequencyDisplay: data.recurring.frequency_display,
            weekdaysDisplay: data.recurring.weekdays_display,
          }
        : null,
      remarks: data.remarks,
      requestingUnit: data.requesting_unit,
      requestingUnitDisplay: data.requesting_unit_display,
      routes: data.routes?.map((route: IBookingRouteApi, idx: number) => ({
        driveId: route.drive_id,
        dropoffDate: route.dropoff_date ? route.dropoff_date : '',
        dropoffLocation: route.dropoff_location,
        dropoffTime: route.dropoff_time
          ? formatDateString(route.dropoff_time, 'HH:mm:ss', 'HH:mm')
          : '',
        dropoffTown: route.dropoff_town,
        id: route.uuid,
        isActive: data.drives.length ? data.drives[idx]?.is_active : true,
        pickupDate: route.pickup_date ? route.pickup_date : '',
        pickupLocation: route.pickup_location,
        pickupTime: route.pickup_time
          ? formatDateString(route.pickup_time, 'HH:mm:ss', 'HH:mm')
          : '',
        pickupTown: route.pickup_town,
        routeNumber: idx + 1,
        signatureCode: route.signature_code,
      })),
      state: data.state as BookingState,
      stateDisplay: data.state_display,
      transferType: data.transfer_type,
      transferTypeDisplay: data.transfer_type_display,
      typeOfTrip: data.type_of_trip,
      typeOfTripDisplay: data.type_of_trip_display,
    };
  }

  // Feedback ============================================================
  public static formatFeedback(data: IFeedbacksListApi): IFeedbacksList {
    return {
      driver: {
        lastName: data.driver?.last_name,
        name: data.driver.name,
      },
      dropoffLocation: data.dropoff_location,
      feedback: {
        comment: data.feedback?.comment,
        created: data.feedback?.created,
        id: data.feedback?.uuid,
        lastStatusChangeBy: data.feedback?.last_status_change_by,
        rate: data.feedback?.rate,
        remarks: data.feedback?.remarks.map((item) => ({
          created: item.created,
          remark: item.remark,
          remarkedBy: item.remarked_by,
          status: item.status,
        })),
        status: data.feedback?.status,
      },
      id: data.uuid,
      isRideShared: data.is_ride_shared,
      pickupDate: data.pickup_date,
      pickupLocation: data.pickup_location,
      vehicle: {
        plateNumber: data.vehicle?.plate_number,
      },
    };
  }

  // Financial ===========================================================
  public static formatDriveCosts(data: IDriveCostsApi): IDriveCosts {
    return {
      agency: data.agency,
      averageKmPerHour: data.average_km_per_hour,
      averageMileage: data.average_mileage,
      basicPercentage: data.basic_percentage,
      carpoolingPercentage: data.carpooling_percentage,
      dieselCost: data.diesel_cost,
      durationCost: data.duration_cost,
      grossSalary: data.gross_salary,
      id: data.uuid,
      kgCo2EmissionDiesel: data.kg_co_2_emission_per_liter_diesel,
      kgCo2EmissionPetrol: data.kg_co_2_emission_per_liter_petrol,
      location: data.location,
      maintenanceCost: data.maintenance_cost,
      perHour: data.per_hour,
      perKmDiesel: data.per_km_diesel,
      perKmPetrol: data.per_km_petrol,
      percentageOfBuffer: data.percentage_of_buffer,
      petrolCost: data.petrol_cost,
      rideSharingPercentage: data.ride_sharing_percentage,
    };
  }

  public static formatIndividualInvoiceTable(
    data: IIndividualInvoiceTableApi,
  ): IIndividualInvoiceTable {
    return {
      dispatchDate: data.dispatch_date,
      firstName: data.first_name,
      id: data.id,
      driveSet: data.drive_set?.map((driveSet) => ({
        driver: driveSet.driver,
        driverAgency: driveSet.driver_agency,
        duration: driveSet.duration,
        greenFund: driveSet.green_fund,
        id: driveSet.id,
        km: driveSet.km,
        price: driveSet.price,
        vehicle: driveSet.vehicle,
      })),
      lastName: data.last_name,
      nOfPassengers: data.n_of_passengers,
      paxAgency: data.pax_agency,
      pdf: data.pdf,
      pickupDate: data.pickup_date,
      requestingUnit: data.requesting_unit,
      transferType: data.transfer_type,
      typeOfTrip: data.type_of_trip,
    };
  }

  public static formatIndividualInvoiceDetails(
    data: IIndividualInvoiceDetailsApi,
  ): IIndividualInvoiceDetails {
    return {
      agency: data?.agency_fk
        ? {
            completeName: data.agency_fk.complete_name,
            email: data.agency_fk.email,
            id: data.agency_fk.uuid,
            shortName: data.agency_fk.short_name,
          }
        : null,
      dispatchDate: data.dispatch_date,
      driveSet: data.drive_set.map((driveSet) => ({
        driver: driveSet.driver,
        driverAgency: driveSet.driver_agency,
        dropoffDate: driveSet.dropoff_date,
        dropoffLocation: driveSet.dropoff_location,
        dropoffTime: driveSet.dropoff_time,
        dropoffTown: driveSet.dropoff_town,
        duration: driveSet.duration,
        greenFund: driveSet.green_fund,
        id: driveSet.id,
        km: driveSet.km,
        pickupDate: driveSet.pickup_date,
        pickupLocation: driveSet.pickup_location,
        pickupTime: driveSet.pickup_time,
        pickupTown: driveSet.pickup_town,
        price: driveSet.price,
        vehicle: driveSet.vehicle,
      })),
      email: data.email,
      firstName: data.first_name,
      flightArrivalDepartureTime: data.flight_arrival_departure_time,
      flightNumber: data.flight_number,
      id: data.id,
      indexNumber: data.index_number,
      lastName: data.last_name,
      nOfPpassengers: data.n_of_passengers,
      phoneNumber: data.phone_number,
      purpose: data.purpose,
      purposeDisplay: data.purpose_display,
      remarks: data.remarks,
      requestingUnit: data.requesting_unit,
      requestingUnitDisplay: data.requesting_unit_display,
      transferType: data.transfer_type,
      transferTypeDisplay: data.transfer_type_display,
      typeOfTrip: data.type_of_trip,
      typeOfTripDisplay: data.type_of_trip_display,
    };
  }

  public static formatInvoiceHistory(data: IHistoryInvoiceApi): IHistoryInvoice {
    return {
      ...data,
      invoiceNumber: data.invoice_number,
      createdBy: data.created_by,
      paymentMethod: data.payment_method,
      invoiceCurrency: data.invoice_currency,
      lastName: data.last_name,
      firstName: data.first_name,
      referenceDate: data.reference_date,
      creationDate: data.creation_date,
      invoiceTotal: data.invoice_total,
      bookingState: data.booking_state,
      invoiceCurrencyName: data.invoice_currency_name,
      requestingUnit: data.requesting_unit,
      transferType: data.transfer_type,
      totalKm: data.total_km,
      totalDuration: data.total_duration,
      totalNumberOfPassengers: data.total_number_of_passengers,
      retentionStatus: data.retention_status,
    };
  }

  public static formatVehicleCosts(data: IVehicleCostsApi): IVehicleCosts {
    return {
      averageKmPerHour: data.average_km_per_hour,
      averageMileage: data.average_mileage,
      body: data.body,
      driveCost: data.drive_cost,
      durationCost: data.duration_cost,
      fuelType: data.fuel_type,
      id: data.uuid,
      kgCo2Emission: data.kg_co_2_emission_per_liter,
      maintenanceCost: data.maintenance_cost,
      perHour: data.per_hour,
      perKm: data.per_km,
      percentageOfBuffer: data.percentage_of_buffer,
    };
  }

  // Mobility ============================================================
  public static formatDriver(data: IDriverApi): IDriver {
    return {
      agency: data.agency
        ? {
            completeName: data.agency.complete_name,
            email: data.agency.email,
            id: data.agency.uuid,
            logo: data.agency.logo_url,
            shortName: data.agency.short_name,
          }
        : null,
      bloodGroup: data.blood_group,
      bloodGroupDisplay: data.blood_group_display || '',
      currentUnavailability: data.current_unavailability
        ? {
            id: data.current_unavailability.uuid,
            sinceDate: data.current_unavailability.since_date,
            sinceTime: data.current_unavailability.since_time,
            untilDate: data.current_unavailability.until_date,
            untilTime: data.current_unavailability.until_time,
          }
        : null,
      email: data.email || '',
      id: data.uuid,
      lastName: data.last_name,
      licenseExpirationDate: data.license_expiration_date
        ? formatDateString(data.license_expiration_date, 'yyyy-MM-dd', 'dd/MM/yyyy')
        : '',
      licenseNumber: data.license_number || '',
      name: data.name || '',
      phoneNumber: data.phone_number || '',
      photo: data.photo,
      preferredVehicle: data.preferred_vehicle || '',
      sharedResources: data.shared_resources || [],
    };
  }

  public static formatDriverDetails(data: IDriverDetailsApi): IDriverDetails {
    return {
      agency: data.agency
        ? {
            completeName: data.agency.complete_name,
            email: data.agency.email,
            id: data.agency.uuid,
            shortName: data.agency.short_name,
          }
        : null,
      bloodGroup: data.blood_group,
      bloodGroupDisplay: data.blood_group_display || '',
      currentUnavailability: data.current_unavailability
        ? {
            id: data.current_unavailability.uuid,
            sinceDate: data.current_unavailability.since_date,
            sinceTime: data.current_unavailability.since_time,
            untilDate: data.current_unavailability.until_date,
            untilTime: data.current_unavailability.until_time,
          }
        : null,
      email: data.email || '',
      id: data.uuid,
      lastModifiedDate: data.last_modified_date
        ? formatDateString(data.last_modified_date, 'dd/MM/yyyy HH:mm', 'MMM. dd, yyyy')
        : '',
      lastName: data.last_name || '',
      licenseExpirationDate: data.license_expiration_date
        ? formatDateString(data.license_expiration_date, 'yyyy-MM-dd', 'dd/MM/yyyy')
        : '',
      licenseNumber: data.license_number || '',
      name: data.name || '',
      phoneNumber: data.phone_number || '',
      photo: data.photo,
      preferredVehicle: data.preferred_vehicle,
      sharedResources: data.shared_resources || [],
    };
  }

  public static formatVehicle(data: IVehicleApi): IVehicle {
    return {
      agency: data.agency
        ? {
            completeName: data.agency.complete_name,
            email: data.agency.email,
            id: data.agency.uuid,
            logo: data.agency.logo_url,
            shortName: data.agency.short_name,
          }
        : null,
      chassisNumber: data.chassis_number,
      connectedToGps: data.connected_to_gps,
      currentUnavailability: data.current_unavailability
        ? {
            id: data.current_unavailability.uuid,
            reasonLabel: data.current_unavailability.reason_label,
            sinceDate: data.current_unavailability.since_date,
            sinceTime: data.current_unavailability.since_time,
            untilDate: data.current_unavailability.until_date,
            untilTime: data.current_unavailability.until_time,
          }
        : null,
      gvlpNumber: data.gvlp_number,
      id: data.uuid,
      isActive: data.is_active,
      label: data.label,
      maxCapacity: data.max_capacity,
      model: data.model
        ? {
            name: data.model.full_name,
            id: data.model.uuid,
            image: data.model.image,
          }
        : null,
      plateNumber: data.plate_number,
      preferredDriver: data.preferred_driver
        ? {
            id: data.preferred_driver.uuid,
            lastName: data.preferred_driver.last_name,
            name: data.preferred_driver.name,
          }
        : null,
      protection: data.protection,
      roofNumber: data.roof_number,
      sharedResources: data.shared_resources,
      totalKm: data.total_km,
      vehicleOwner: data.vehicle_owner,
    };
  }

  public static formatVehicleDetails(data: IVehicleDetailsApi): IVehicleDetails {
    return {
      agency: data.agency
        ? {
            completeName: data.agency.complete_name,
            email: data.agency.email,
            id: data.agency.uuid,
            logo: data.agency.logo_url,
            shortName: data.agency.short_name,
          }
        : null,
      chassisNumber: data.chassis_number || '',
      connectedToGps: data.connected_to_gps,
      currentUnavailability: data.current_unavailability
        ? {
            id: data.current_unavailability.uuid,
            sinceDate: data.current_unavailability.since_date,
            sinceTime: data.current_unavailability.since_time,
            untilDate: data.current_unavailability.until_date,
            untilTime: data.current_unavailability.until_time,
          }
        : null,
      gvlpNumber: data.gvlp_number || '',
      id: data.uuid,
      isActive: data.is_active,
      label: data.label,
      lastModifieDate: data.last_modified_date,
      maxCapacity: data.max_capacity || 1,
      model: data.model
        ? {
            name: data.model.full_name,
            id: data.model.uuid,
            image: data.model.image,
          }
        : null,
      modelChoices: data.model_choices,
      plateNumber: data.plate_number || '',
      preferredDriver: data.preferred_driver,
      protection: data.protection,
      roofNumber: data.roof_number || '',
      sharedResources: data.shared_resources || [],
      totalKm: data.total_km,
      vehicleOwner: data.vehicle_owner,
    };
  }

  // Mobility Planner =============================================================
  public static formatDrives(data: IPlannerDrivesApi): IPlannerDrives {
    return {
      driverAgency: data.driver_agency,
      driverEmail: data.driver_email,
      driverFirstName: data.driver_first_name,
      driverId: data.driver_id,
      driverLastName: data.driver_last_name,
      driverPhone: data.driver_phone_number,
      dropoffDate: data.dropoff_date,
      dropoffLocation: data.dropoff_location,
      dropoffTime: data.dropoff_time,
      dropoffTown: data.dropoff_town,
      editableState: data.editable_state,
      id: data.uuid,
      isConflicting: data.is_conflicting,
      passengerAgency: data.passenger_agency,
      passengerEmail: data.passenger_email,
      passengerFirstName: data.passenger_first_name,
      passengerLastName: data.passenger_last_name,
      passengerPax: data.passenger_number_of_pax,
      passengerPhone: data.passenger_phone_number,
      passengerRemarks: data.passenger_remarks ?? '',
      pickupDate: data.pickup_date,
      pickupLocation: data.pickup_location,
      pickupTime: data.pickup_time,
      pickupTown: data.pickup_town,
      vehicleAgency: data.vehicle_agency,
      vehicleId: data.vehicle_id,
    };
  }

  public static formatDrivesToPlannerEvents(data: IPlannerDrivesApi): IPlannerEvent {
    return {
      className: Serializer.getEventClassName(data.editable_state),
      end: moment(`${data.dropoff_date} ${data.dropoff_time}`, 'DD/MM/YYYY HH:mm:ss').toDate(),
      id: data.uuid,
      resourceId: data.vehicle_id,
      start: moment(`${data.pickup_date} ${data.pickup_time}`, 'DD/MM/YYYY HH:mm:ss').toDate(),
      title: data.driver_first_name,
      extendedProps: Serializer.formatDrives(data),
    };
  }

  public static formatFacilityDriver(data: FacilityDriver): FacilityDriverMapped {
    return {
      driverAgency: data.driver_agency,
      driverId: data.driver_identifier,
      email: data.email,
      lastName: data.last_name,
      name: data.name,
      phoneNumber: data.phone_number,
      shared: data.shared,
    };
  }

  public static formatFacilityVehicle(data: FacilityVehicle): FacilityVehicleMapped {
    return {
      agencyLogo: data.agency_logo,
      image: data.image,
      isDisabled: data.is_disabled,
      label: data.label,
      maxCapacity: data.max_capacity,
      plateNumber: data.plate_number,
      preferredDriver: data.preferred_driver,
      protection: data.protection,
      shared: data.shared,
      sharedAgency: data.shared_agency,
      vehicleAgency: data.vehicle_agency,
      vehicleId: data.vehicle_identifier,
    };
  }

  public static mapFormDriveEditToUpdateDrive(
    data: IFormDriveEdit,
    drive: IBookingDrive,
  ): IUpdateDrive {
    return {
      driver: data.driver.value,
      dropoff_date: moment(data.dropoffDate).format(DATE_FORMAT.API),
      dropoff_location: drive.dropoffLocation,
      dropoff_time: moment(data.dropoffTime).format('HH:mm'),
      dropoff_town: drive.dropoffTown,
      is_active: drive.isActive,
      pickup_date: moment(data.pickupDate).format(DATE_FORMAT.API),
      pickup_location: drive.pickupLocation,
      pickup_time: moment(data.pickupTime).format('HH:mm'),
      pickup_town: drive.pickupTown,
      uuid: drive.id,
      vehicle: data.vehicle.value,
    };
  }

  public static formatVehiclesToPlannerResources(data: IPlannerVehiclesApi): IPlannerResources {
    return {
      agencyLogo: data.agency_logo,
      id: data.vehicle_identifier,
      image: data.image,
      isDisabled: data.is_disabled,
      maxCapacity: data.max_capacity,
      plateNumber: data.plate_number,
      preferredDriver: data.preferred_driver,
      protection: data.protection,
      shared: data.shared,
      sharedAgency: data.shared_agency,
      title: data.label,
      vehicleAgency: data.vehicle_agency,
    };
  }

  // Reports =============================================================
  public static formatCountryReport(data: ICountryReportApi): ICountryReport {
    return {
      agencyPax: data.agency_pax,
      airportDrives: data.airport_drives,
      bookingsCount: data.bookings_count,
      carpooledDrives: data.carpooled_drives,
      carpooledPax: data.carpooled_pax,
      co2saving: data.co_2_saving,
      drivers: data.drivers,
      drives: data.drives,
      efficiencySaving: data.efficiency_saving,
      inTownDrives: data.in_town_drives,
      locationName: data.location_name,
      outOfTownDrives: data.out_of_town_drives,
      pax: data.pax,
      referenceDate: data.reference_date,
      rideSharedDrives: data.ride_shared_drives,
      rideSharedPax: data.ride_shared_pax,
      totalDrivesKm: data.total_drives_km,
      totalDrivesTime: data.total_drives_time,
      vehicles: data.vehicles,
    };
  }

  public static formatDriveWithWorkflow(data: IDriveWithWorkflowApi): IDriveWithWorkflow {
    return {
      endDate: data.end_date,
      endKm: data.end_km,
      endTime: data.end_time,
      isRideShared: data.is_ride_shared,
      sharedDrives: data.shared_drives?.map((drive: ISharedDrivesApi) => ({
        bookingRef: drive.booking_ref,
        duration: drive.duration,
        mileage: drive.mileage,
        vehicleLabel: drive.vehicle_label,
      })),
      startDate: data.start_date,
      startKm: data.start_km,
      startTime: data.start_time,
    };
  }

  public static formatMonthlySummary(data: IMonthlySummaryApi): IMonthlySummary {
    return {
      airport: data.airport,
      amount: data.amount || '0.0',
      bookingsCount: data.bookings_count,
      drives: data.drives,
      greenFund: data.green_fund || '0.0',
      inTown: data.in_town,
      outOfTown: data.out_of_town,
      totalMileage: data.total_mileage || '0',
      totalTime: data.total_time || '00:00',
    };
  }

  public static formatReportDetails(data: IReportDetailsListApi): IReportDetailsList {
    return {
      agency: data.agency,
      bookingId: data.booking_id,
      costCategory: data.cost_category,
      driverAgency: data.driver_agency,
      driverFullName: data.driver_full_name,
      duration: data.duration,
      hasWorkflow: data.has_workflow,
      id: data.uuid,
      isCarpooled: data.is_carpooled,
      isEditableByFocalPoint: data.is_editable_by_focal_point,
      isManualEdited: data.is_manual_edited,
      isRideShared: data.is_ride_shared,
      isSynced: data.is_synced,
      km: data.km,
      passenger: data.passenger,
      pax: data.pax,
      pickupDate: data.pickup_date,
      price: data.price,
      transferType: data.transfer_type,
      unit: data.unit,
      vehicle: data.vehicle,
    };
  }

  public static formatSummary(data: ISummaryListApi): ISummaryList {
    return {
      agency: data.agency,
      agencyId: data.agency_id,
      airport: data.airport,
      amount: data.amount,
      bookingsCount: data.bookings_count,
      carpooledDrives: data.carpooled_drives,
      carpooledPax: data.carpooled_pax,
      drives: data.drives,
      greenFund: data.green_fund,
      inTown: data.in_town,
      outOfTown: data.out_of_town,
      pdf: data.pdf || '',
      rideSharedDrives: data.ride_shared_drives,
      rideSharedPax: data.ride_shared_pax,
      totalMileage: data.total_mileage,
      totalTime: data.total_time,
    };
  }
}

export default Serializer;
